import dtfIndexStakingVault from '@/abis/dtf-index-staking-vault'
import TokenLogoWithChain from '@/components/token-logo/TokenLogoWithChain'
import { Button } from '@/components/ui/button'
import DataTable from '@/components/ui/data-table'
import { formatCurrency, formatToSignificantDigits, formatUSD } from '@/utils'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { ColumnDef } from '@tanstack/react-table'
import { Trans, useLingui } from '@lingui/react/macro'
import { useAtomValue } from 'jotai'
import { Gift } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { walletAtom, walletChainAtom } from '@/state/atoms'
import { toast } from 'sonner'
import { Address } from 'viem'
import {
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi'
import { portfolioRewardsAtom } from '../atoms'
import { ExpandToggle, useExpandable } from './expand-toggle'
import SectionHeader from './section-header'

type RewardRow = {
  address: Address
  chainId: number
  symbol: string
  name: string
  decimals: number
  amount: string
  value: number
  stTokenAddress: Address
}

const ClaimButton = ({ reward }: { reward: RewardRow }) => {
  const { t } = useLingui()
  const wallet = useAtomValue(walletAtom)
  const walletChain = useAtomValue(walletChainAtom)
  const { openConnectModal } = useConnectModal()
  const { switchChainAsync } = useSwitchChain()
  const { writeContract, data: hash, isPending } = useWriteContract()
  const { data: receipt } = useWaitForTransactionReceipt({
    hash,
    chainId: reward.chainId,
  })
  const claimed = receipt?.status === 'success'

  useEffect(() => {
    if (claimed) {
      toast.success(t`Rewards claimed`, { duration: 8000 })
    }
  }, [claimed])

  const loading = !receipt && (isPending || !!hash)

  const handleClick = useCallback(async () => {
    if (!wallet) {
      openConnectModal?.()
      return
    }
    if (walletChain !== reward.chainId) {
      await switchChainAsync?.({ chainId: reward.chainId })
    }
    writeContract({
      abi: dtfIndexStakingVault,
      functionName: 'claimRewards',
      address: reward.stTokenAddress,
      args: [[reward.address]],
      chainId: reward.chainId,
    })
  }, [wallet, walletChain, reward, openConnectModal, switchChainAsync, writeContract])

  return (
    <Button
      onClick={handleClick}
      disabled={claimed || loading}
      className="rounded-full text-sm"
      size="sm"
    >
      {claimed ? <Trans>Claimed</Trans> : <Trans>Claim</Trans>}
    </Button>
  )
}

const columns: ColumnDef<RewardRow, any>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: () => <Trans>Reward Token</Trans>,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <TokenLogoWithChain
          symbol={row.original.symbol}
          address={row.original.address}
          chain={row.original.chainId}
        />
        <p className="font-bold text-sm">{row.original.symbol}</p>
      </div>
    ),
  },
  {
    id: 'balance',
    accessorKey: 'amount',
    header: () => <Trans>Balance</Trans>,
    cell: ({ row }) => {
      const val = Number(row.original.amount)
      return (
        <span className="text-sm whitespace-nowrap">
          {!isNaN(val) ? formatToSignificantDigits(val) : '—'}
        </span>
      )
    },
  },
  {
    id: 'value',
    accessorKey: 'value',
    header: () => <Trans>Value</Trans>,
    cell: ({ row }) => {
      const val = row.original.value
      return (
        <span className="text-sm font-bold whitespace-nowrap">
          {val != null && !isNaN(val) ? formatUSD(val) : '—'}
        </span>
      )
    },
  },
  {
    id: 'actions',
    header: () => <Trans>Claim</Trans>,
    cell: ({ row }) => <ClaimButton reward={row.original} />,
    meta: { className: 'text-right' },
  },
]

const AvailableRewards = () => {
  const rewards = useAtomValue(portfolioRewardsAtom)

  const { displayData, expanded, toggle, hasMore, total } =
    useExpandable(rewards)

  if (!rewards.length) return null

  return (
    <div id="available-rewards">
      <SectionHeader
        icon={Gift}
        title={<Trans>Available Rewards</Trans>}
        subtitle={
          <Trans>
            Earn rewards by staking or participating in governance.{' '}
            <a
              href="https://docs.reserve.org/core-components/index-dtfs/roles"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              Learn more
            </a>
            .
          </Trans>
        }
      />
      <div className="bg-card rounded-[20px] border border-border overflow-hidden">
        <DataTable columns={columns} data={displayData} />
        {hasMore && (
          <ExpandToggle expanded={expanded} total={total} onToggle={toggle} />
        )}
      </div>
    </div>
  )
}

export default AvailableRewards
